import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, Cog6ToothIcon, BellIcon } from '@heroicons/react/24/outline';
import {
  WelcomeHeader,
  SobrietyCard,
  QuickActions,
  DailyReflectionCard,
  ProgressStats,
  SponsorReminderModal,
  InsightsWidget,
  PendingHomeworkWidget,
} from '../components/features/dashboard';
import { ThemeToggleSimple, Avatar, LogoIcon, toast } from '../components/ui';
import { MountainBackground } from '../components/layout';
import { NotificationsPanel } from '../components/features/sponsor/NotificationsPanel';
import { useOnboarding, useAuth, useReminders } from '../stores';
import { useCheckinStore } from '../stores/checkinStore';
import { ActivityWidget } from '../components/features/activity/ActivityWidget';
import { ServiceCommitmentsCard } from '../components/features/dashboard/ServiceCommitmentsCard';
import { ThreeCirclesCard } from '../components/features/dashboard/ThreeCirclesCard';
import { useActivityStore } from '../stores/activityStore';
import { getUserProfile } from '../services/firebase';
import { getUserStepProgress } from '../services/firebase/stepWork';
import {
  getUnreadNotificationCount,
  checkPendingSharesOnLogin,
} from '../services/firebase/templateSharing';

export function DashboardPage() {
  const navigate = useNavigate();
  const { data } = useOnboarding();
  const { logout, user } = useAuth();
  const { shouldShowReminder, dismissReminder } = useReminders();
  const { subscribeTodayCheckIn, subscribeStreaks, unsubscribeAll, streaks } = useCheckinStore();
  const { definitions, currentLog } = useActivityStore();

  // Derive meetingsThisWeek from activity log completions
  const meetingsDef = definitions.find(d => d.name.toLowerCase().includes('meeting') && !d.isAutoTracked);
  const meetingsThisWeek = meetingsDef
    ? (currentLog?.completions[meetingsDef.id] ?? []).length
    : 0;

  // Derive gratitude completions this week
  const gratitudeDef = definitions.find(d => d.name.toLowerCase().includes('gratitude'));
  const gratitudeThisWeek = gratitudeDef
    ? (currentLog?.completions[gratitudeDef.id] ?? []).length
    : 0;
  const [currentStep, setCurrentStep] = useState(1);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSponsorReminder, setShowSponsorReminder] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('Friend');
  const [unreadCount, setUnreadCount] = useState(0);

  // Load user profile for avatar and name
  useEffect(() => {
    async function loadUserProfile() {
      if (!user?.uid) return;

      try {
        const profile = await getUserProfile(user.uid);
        if (profile) {
          setUserName(`${profile.firstName} ${profile.lastInitial}.`);
          setFirstName(profile.firstName);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    }

    loadUserProfile();
  }, [user?.uid]);

  // Load current step progress
  useEffect(() => {
    if (!user?.uid) return;
    getUserStepProgress(user.uid)
      .then(progress => {
        if (progress.length === 0) return;
        const active = progress.filter(p => p.status === 'in_progress' || p.status === 'pending_review');
        if (active.length > 0) {
          setCurrentStep(Math.max(...active.map(p => p.stepNumber)));
        } else {
          setCurrentStep(Math.min(Math.max(...progress.map(p => p.stepNumber)), 12));
        }
      })
      .catch(console.error);
  }, [user?.uid]);

  // Check for pending shares on login
  useEffect(() => {
    async function checkShares() {
      if (!user?.uid || !user?.email) return;

      try {
        await checkPendingSharesOnLogin(user.uid, user.email);
      } catch (error) {
        console.error('Error checking pending shares:', error);
      }
    }

    checkShares();
  }, [user?.uid, user?.email]);

  // Load unread notification count
  useEffect(() => {
    async function loadUnreadCount() {
      if (!user?.uid) return;

      try {
        const count = await getUnreadNotificationCount(user.uid);
        setUnreadCount(count);
      } catch (error) {
        console.error('Error loading unread count:', error);
      }
    }

    loadUnreadCount();

    // Refresh count every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user?.uid]);

  // Subscribe to real-time check-in data on mount
  useEffect(() => {
    console.log('DashboardPage mounted - subscribing to check-in listeners');
    subscribeTodayCheckIn();
    subscribeStreaks();

    return () => {
      console.log('DashboardPage unmounting - cleaning up check-in listeners');
      unsubscribeAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount

  // Check if we should show the sponsor reminder on mount
  useEffect(() => {
    if (shouldShowReminder('sponsor-setup')) {
      // Delay showing the modal slightly so the page loads first
      const timer = setTimeout(() => {
        setShowSponsorReminder(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowReminder]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleAddSponsor = () => {
    setShowSponsorReminder(false);
    dismissReminder('sponsor-setup');
    // TODO: Navigate to sponsor connection page
    toast.info('Feature coming soon', 'Sponsor connection page is under development.');
  };

  const handleRemindLater = () => {
    setShowSponsorReminder(false);
    dismissReminder('sponsor-setup');
  };

  const handleCloseSponsorReminder = () => {
    setShowSponsorReminder(false);
    // Don't dismiss - just close for this session
  };

  // Calculate days sober from first sobriety date (if any)
  const firstSobrietyDate = data.sobrietyDates[0];
  const daysSober = firstSobrietyDate
    ? Math.floor((new Date().getTime() - firstSobrietyDate.startDate.getTime()) / (1000 * 60 * 60 * 24))
    : undefined;

  return (
    <MountainBackground variant="subtle">
      <div className="min-h-screen bg-surface-50/90 dark:bg-surface-900/90">
        {/* Sponsor reminder modal */}
        <SponsorReminderModal
          isOpen={showSponsorReminder}
          onClose={handleCloseSponsorReminder}
        onAddSponsor={handleAddSponsor}
        onRemindLater={handleRemindLater}
      />

      {/* Header with user menu */}
      <div className="md:hidden px-4 pt-4 pb-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoIcon size="md" />
            <h1 className="font-display text-xl font-semibold text-navy-800 dark:text-gold-500">
              Daily Reprieve
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Theme toggle */}
            <ThemeToggleSimple />

            {/* Notifications */}
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              <BellIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="p-1 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
              >
                <Avatar
                  name={userName || user?.displayName || user?.email || undefined}
                  size="sm"
                  variant="brand"
                />
              </button>

              {showUserMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  />
                  {/* Dropdown */}
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-surface-800 rounded-xl shadow-lg border border-surface-200 dark:border-surface-700 py-2 z-50">
                    <div className="px-4 py-2 border-b border-surface-200 dark:border-surface-700">
                      <p className="text-sm font-medium text-surface-900 dark:text-white truncate">
                        {user?.email || 'Dev User'}
                      </p>
                      <p className="text-xs text-surface-500 dark:text-surface-400">
                        {user?.uid?.startsWith('dev-') ? 'Development Mode' : 'Signed in'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700"
                    >
                      <Cog6ToothIcon className="w-4 h-4" />
                      Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-surface-100 dark:hover:bg-surface-700"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main id="main-content" className="max-w-4xl mx-auto px-4 py-6 pb-24 space-y-6">
        {/* Welcome header */}
        <WelcomeHeader firstName={firstName} daysSober={daysSober} />

        {/* Quick actions */}
        <section>
          <h2 className="text-sm font-semibold text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-3">
            Quick Actions
          </h2>
          <QuickActions />
        </section>

        {/* Sobriety counters */}
        {data.sobrietyDates.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-3">
              Sobriety Tracking
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.sobrietyDates.map((sd, index) => (
                <SobrietyCard
                  key={`${sd.label}-${index}`}
                  label={sd.label}
                  startDate={sd.startDate}
                />
              ))}
            </div>
          </section>
        )}

        {/* Today's Insight Widget */}
        <section>
          <InsightsWidget />
        </section>

        {/* Pending Homework */}
        {user?.uid && (
          <section>
            <PendingHomeworkWidget userId={user.uid} />
          </section>
        )}

        {/* Service Commitments — Step 12 */}
        {user?.uid && (
          <section>
            <ServiceCommitmentsCard uid={user.uid} />
          </section>
        )}

        {/* Three Circles — Recovery Boundaries */}
        {user?.uid && (
          <section>
            <ThreeCirclesCard uid={user.uid} />
          </section>
        )}

        {/* Activity Tracker */}
        <section>
          <h2 className="text-sm font-semibold text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-3">
            This Week
          </h2>
          <ActivityWidget />
        </section>

        {/* Progress stats */}
        <section>
          <h2 className="text-sm font-semibold text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-3">
            Your Progress
          </h2>
          <ProgressStats
            checkInStreak={streaks?.morning?.current ?? 0}
            meetingsThisWeek={meetingsThisWeek}
            gratitudeStreak={gratitudeThisWeek}
            stepProgress={currentStep}
          />
        </section>

        {/* Daily reflection */}
        <section>
          <h2 className="text-sm font-semibold text-surface-500 dark:text-surface-400 uppercase tracking-wide mb-3">
            Daily Reflection
          </h2>
          <DailyReflectionCard />
        </section>
      </main>



      {/* Notifications Panel */}
      {user?.uid && (
        <NotificationsPanel
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          userId={user.uid}
          onRefresh={async () => {
            // Refresh unread count after actions
            const count = await getUnreadNotificationCount(user.uid);
            setUnreadCount(count);
          }}
        />
      )}
      </div>
    </MountainBackground>
  );
}
